/**
 * Skeleton — единый shimmer-плейсхолдер для прогрессивной загрузки.
 *
 * Заменяет полноэкранный крутящийся круг: лёгкий каркас страницы виден сразу, а
 * тяжёлый PS2-блок (оборот/маржа/тренд ~10 c) показывает skeleton ВНУТРИ своей
 * панели, не блокируя остальную вкладку.
 *
 *   <Skeleton width={120} height={22} />           // строка/значение
 *   <Skeleton width="100%" height={8} radius={5} /> // трек бара
 *   <KpiTileSkeleton />                             // карточка KPI
 *   <SkeletonRows count={5} height={14} />          // список строк
 */
import '../../styles/skeleton.css'

const px = (v) => (typeof v === 'number' ? `${v}px` : v)

export function Skeleton({ width = '100%', height = 12, radius = 6, style, className = '' }) {
    return (
        <span
            aria-hidden="true"
            className={`skeleton ${className}`.trim()}
            style={{ width: px(width), height: px(height), borderRadius: px(radius), ...style }}
        />
    )
}

// KPI-плитка в состоянии загрузки: та же геометрия, что KpiTile (card + padding),
// чтобы каркас не «прыгал» при появлении данных.
export function KpiTileSkeleton() {
    return (
        <div className="card" style={{ padding: '12px 14px', minHeight: 64, display: 'flex', flexDirection: 'column', gap: 8, justifyContent: 'center' }}>
            <Skeleton width={90} height={22} radius={6} />
            <Skeleton width={64} height={10} radius={4} />
        </div>
    )
}

// N строк-плейсхолдеров с ритмичными отступами — для таблиц/списков/распределений.
export function SkeletonRows({ count = 4, height = 14, gap = 10, widths }) {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap }}>
            {Array.from({ length: count }, (_, i) => (
                <Skeleton key={i} height={height} width={widths ? widths[i % widths.length] : '100%'} />
            ))}
        </div>
    )
}

// Карточка-плейсхолдер в геометрии entity-card (§17: иконка-якорь 40px + title +
// строка мет). N таких карточек = каркас списка на первую отрисовку, чтобы вместо
// полноэкранного спиннера сразу был виден силуэт страницы (Linear/Stripe pattern).
export function SkeletonCardList({ count = 5, gap = 8 }) {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap }} aria-hidden="true">
            {Array.from({ length: count }, (_, i) => (
                <div key={i} className="card" style={{ padding: '16px 20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                        <Skeleton width={40} height={40} radius={12} />
                        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <Skeleton width="42%" height={15} radius={5} />
                            <Skeleton width="68%" height={11} radius={4} />
                        </div>
                        <Skeleton width={72} height={24} radius={999} />
                    </div>
                </div>
            ))}
        </div>
    )
}

// Каркас дашборда на первую отрисовку: шапка + KPI-ряд + пара карточек. Заменяет
// полноэкранный спиннер «Загружаем данные…» силуэтом страницы. Отступы/воздух — в
// текущих ERP-нормах (плотно, без раздувания), заголовков не рисует. Использует
// готовые утилиты (.page-content / .grid-2-4 / .grid-1-2 / .card).
export function SkeletonDashboard({ kpis = 4 }) {
    return (
        <div className="page-content" aria-hidden="true">
            <Skeleton width={220} height={16} radius={5} style={{ display: 'block', marginBottom: 20 }} />
            <div className="grid-2-4" style={{ marginBottom: 16 }}>
                {Array.from({ length: kpis }, (_, i) => <KpiTileSkeleton key={i} />)}
            </div>
            <div className="card" style={{ padding: '18px 20px', marginBottom: 16 }}>
                <SkeletonRows count={3} widths={['38%', '72%', '54%']} />
            </div>
            <div className="grid-1-2">
                <div className="card" style={{ padding: '18px 20px' }}><SkeletonRows count={4} /></div>
                <div className="card" style={{ padding: '18px 20px' }}><SkeletonRows count={4} /></div>
            </div>
        </div>
    )
}

// Строки-скелетоны для таблиц (§5.1): рендерит <tr>/<td> прямо в <tbody> вместо
// одной ячейки-спиннера «Загрузка…». cols = число колонок таблицы, rows = сколько
// строк-каркасов показать на первую отрисовку.
export function SkeletonTableRows({ cols = 5, rows = 5 }) {
    return (
        <>
            {Array.from({ length: rows }, (_, r) => (
                <tr key={r} aria-hidden="true">
                    {Array.from({ length: cols }, (_, c) => (
                        <td key={c}>
                            <Skeleton height={14} width={c === 0 ? '70%' : c === cols - 1 ? 36 : '55%'} />
                        </td>
                    ))}
                </tr>
            ))}
        </>
    )
}

// Каркас страницы-карточки сущности («кейс»): шапка-аватар + имя/мета + пара
// карточек контента. Заменяет полноэкранный спиннер на детальных страницах
// (сотрудник/вакансия/актив/ключ/заявка). Умеренно, без крупных заголовков.
/**
 * Скелетон карточки. Объявляет состояние `loading` машинно.
 *
 * Без этого скелетон отдаёт сторожу вёрстки ту же геометрию, что и готовая
 * страница: карточки на месте, размеры честные, шиммер бесконечен и потому
 * из ожидания анимаций исключён. Замер скелетона проходил как замер
 * содержимого — вакуумная зелень, спрятанная глубже обычной (волна 3).
 */
export function SkeletonDetail() {
    return (
        <div className="page-content" aria-hidden="true" data-page-state="loading">
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
                <Skeleton width={48} height={48} radius={12} />
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <Skeleton width="34%" height={18} radius={5} />
                    <Skeleton width="20%" height={12} radius={4} />
                </div>
            </div>
            <div className="card" style={{ padding: '18px 20px', marginBottom: 16 }}>
                <SkeletonRows count={4} widths={['40%', '72%', '56%', '64%']} />
            </div>
            <div className="card" style={{ padding: '18px 20px' }}>
                <SkeletonRows count={3} widths={['48%', '68%', '52%']} />
            </div>
        </div>
    )
}

export default Skeleton
